import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Users, Palette, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const GetStartedPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfter = searchParams.get('redirect') || '/dashboard';
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const benefits = [
    {
      icon: <Palette className="w-5 h-5" />,
      title: "AI-Powered Design",
      description: "Get professional WordPress designs in minutes"
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Team Collaboration",
      description: "Create a workspace and invite team members"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "50 Free Credits",
      description: "Start designing immediately, no credit card needed"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please fill in your name');
      return;
    }

    setSubmitting(true);
    try {
      // Store lead in backend
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        source: 'get_started_page'
      });

      // Store in localStorage for post-login welcome
      localStorage.setItem('pendingLead', JSON.stringify(formData));

      // Redirect to Google OAuth (using custom domain)
      window.location.href = `${API}/auth/google/login?redirect=${encodeURIComponent(redirectAfter)}`;
    } catch (error) {
      console.error('Lead capture error:', error);
      // Still proceed to OAuth even if lead capture fails
      window.location.href = `${API}/auth/google/login?redirect=${encodeURIComponent(redirectAfter)}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 flex">
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 to-violet-600 p-12 flex-col justify-between">
        <div>
          <WowCXLogo size="lg" />
        </div>
        
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>
              Design Your Website with AI
            </h1>
            <p className="text-xl text-indigo-100">
              Join thousands of businesses creating stunning WordPress websites in minutes.
            </p>
          </div>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center text-white">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-white">{benefit.title}</h3>
                  <p className="text-sm text-indigo-100">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        <div className="text-indigo-200 text-sm">
          &copy; 2025 Wow!CX. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <WowCXLogo size="lg" className="justify-center" />
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2 text-zinc-900" style={{fontFamily: 'Outfit'}}>
                  Get Started Free
                </h2>
                <p className="text-zinc-600">
                  Tell us about yourself to personalize your experience
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Your Name *
                  </label>
                  <Input
                    data-testid="lead-name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Company Name
                  </label>
                  <Input
                    data-testid="lead-company"
                    placeholder="Acme Inc. (optional)"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Business Email
                  </label>
                  <Input
                    data-testid="lead-email"
                    type="email"
                    placeholder="john@company.com (optional)"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  />
                </div>

                {/* Separator with spacing */}
                <div className="pt-6 mt-2">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-white text-zinc-500">Sign in to continue</span>
                    </div>
                  </div>
                </div>

                {/* Google Sign-in Section */}
                <div className="pt-4 space-y-3">
                  <p className="text-center text-sm font-medium text-zinc-700">
                    Use your Google account to sign in securely
                  </p>
                  
                  <Button
                    data-testid="lead-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {/* Trust Indicators */}
              <div className="mt-8 pt-6 border-t border-zinc-100">
                <div className="flex items-center justify-center gap-6 text-xs text-zinc-500">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    <span>No credit card</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    <span>50 free credits</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    <span>Cancel anytime</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workspace Info */}
          <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-indigo-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-indigo-900 text-sm">Team Collaboration</h4>
                <p className="text-xs text-indigo-700 mt-1">
                  After signing up, you&apos;ll get your own workspace where you can invite team members to collaborate on designs together.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GetStartedPage;
